import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type FormEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { ApiError } from '../net/client';
import {
  categoryLabel,
  createProduct,
  formCategories,
  regulatedCategories,
  updateProduct,
  uploadProductImage,
  type ProductFields,
} from '../net/products';
import { getOrganization } from '../net/reports';
import { useSession } from '../session/useSession';
import { chip, colors, radius, shell, type, verdict } from '../theme';

/**
 * G3, `design/PortalProductForm.dc.html`.
 *
 * Every value below is literal in that board, with its three em dashes made a
 * colon or a comma. The product is created with `POST /v1/products` and the
 * photo sent after it, because the image route needs the product's id. What
 * the board does not draw is D10: where a field message lands, what a failed
 * photo does to a product already created, and that "Other" is not offered.
 */

type Field = 'name' | 'description' | 'category';

type ImageRejection =
  { kind: 'too_small'; width: string; height: string } | { kind: 'message'; message: string };

interface Problems {
  fields: Partial<Record<Field, string>>;
  image?: ImageRejection;
  general?: string;
}

const none: Problems = { fields: {} };

/** A no-break space: an empty line at the step's own height. */
const keepLine = ' ';

export function ProductForm() {
  const { api } = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [organization, setOrganization] = useState('');
  // Set once the product exists. A photo the API then refuses must not create
  // a second product on the next try, so the retry updates this one, D10.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<Problems>(none);

  useEffect(() => {
    const controller = new AbortController();
    // Only the scan preview's second line needs it, so a failure leaves it off.
    getOrganization(api, controller.signal).then(
      (org) => setOrganization(org.name),
      () => {},
    );
    return () => controller.abort();
  }, [api]);

  // The object URL the scan preview shows, released when replaced or left.
  const previewRef = useRef<string | null>(null);
  useEffect(() => () => release(previewRef.current), []);

  function choose(picked: File | undefined) {
    if (!picked) return;
    release(previewRef.current);
    previewRef.current =
      typeof URL.createObjectURL === 'function' ? URL.createObjectURL(picked) : null;
    setPreview(previewRef.current);
    setFile(picked);
    // A new file answers the old rejection.
    setProblems(({ image: _image, ...rest }) => rest);
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    if (saving) return;
    setSaving(true);
    setProblems(none);

    const fields: ProductFields = { name, description, ...(category !== '' ? { category } : {}) };
    let id = createdId;
    try {
      if (id === null) {
        id = (await createProduct(api, fields)).id;
        setCreatedId(id);
      } else {
        await updateProduct(api, id, fields);
      }
    } catch (error) {
      setProblems(fromFields(error));
      setSaving(false);
      return;
    }

    if (file !== null) {
      try {
        await uploadProductImage(api, id, file);
      } catch (error) {
        setProblems(fromImage(error));
        setSaving(false);
        return;
      }
    }
    navigate('/products');
  }

  const subline = [organization, category === '' ? '' : categoryLabel(category)]
    .filter(Boolean)
    .join(' · ');

  return (
    <form onSubmit={(e) => void submit(e)} noValidate style={{ display: 'contents' }}>
      <PageHeader
        title="New product"
        subtitle="This is what a consumer sees after a genuine scan: keep it exactly as printed on the pack"
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="vp-outline"
              type="button"
              onClick={() => navigate('/products')}
              style={cancelButton}
            >
              Cancel
            </button>
            <button
              className="vp-fill"
              type="submit"
              disabled={saving}
              aria-busy={saving}
              style={createButton}
            >
              Create product
            </button>
          </div>
        }
      />

      {problems.general !== undefined ? <Rejection>{problems.general}</Rejection> : null}

      <div
        style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 16, alignItems: 'start' }}
      >
        <div style={column}>
          <div style={cardBox}>
            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={fieldColumn}>
                <label htmlFor="product-name" style={fieldLabel}>
                  Product name
                </label>
                <input
                  id="product-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-invalid={problems.fields.name !== undefined}
                  aria-describedby="product-name-hint"
                  style={input}
                />
                <div id="product-name-hint" style={{ ...type.caption, color: colors.mutedInk }}>
                  Exactly as it appears on the label, including size.
                </div>
                <FieldMessage message={problems.fields.name} />
              </div>
              <div style={fieldColumn}>
                <label htmlFor="product-description" style={fieldLabel}>
                  Description
                </label>
                <textarea
                  id="product-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  aria-invalid={problems.fields.description !== undefined}
                  style={textarea}
                />
                <FieldMessage message={problems.fields.description} />
              </div>
            </div>
          </div>

          <div style={cardBox}>
            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div id="category-title" style={{ ...type.panelTitle, color: colors.navy }}>
                Category
              </div>
              <div style={{ ...type.meta, color: colors.mutedInk, lineHeight: 1.5 }}>
                Regulated categories require a batch number, manufacture date and expiry on every
                batch you issue.
              </div>
              <div
                role="radiogroup"
                aria-labelledby="category-title"
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}
              >
                {formCategories.map((value) => (
                  <CategoryOption
                    key={value}
                    value={value}
                    selected={category === value}
                    onSelect={() => setCategory(value)}
                  />
                ))}
              </div>
              <FieldMessage message={problems.fields.category} />
            </div>
          </div>
        </div>

        <div style={column}>
          <div style={cardBox}>
            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <div style={{ ...type.panelTitle, color: colors.navy }}>Product photo</div>
                <span style={{ ...pill, background: chip.brand.bg, color: chip.brand.fg }}>
                  Required
                </span>
              </div>
              <label
                onDragOver={(e: DragEvent) => e.preventDefault()}
                onDrop={(e: DragEvent) => {
                  e.preventDefault();
                  choose(e.dataTransfer.files[0]);
                }}
                style={dropZone}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  aria-label="Product photo"
                  onChange={(e) => choose(e.target.files?.[0])}
                  style={visuallyHidden}
                />
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect
                    x="3"
                    y="5"
                    width="18"
                    height="14"
                    rx="2.5"
                    stroke={colors.mutedInk}
                    strokeWidth="1.6"
                  />
                  <circle cx="8.5" cy="10" r="1.6" stroke={colors.mutedInk} strokeWidth="1.6" />
                  <path
                    d="M4 17 L9.5 12 L13 15 L16 13 L20 16.5"
                    stroke={colors.mutedInk}
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {/* A chosen file is not drawn: its name takes the prompt's line, D10. */}
                <div style={{ ...type.bodyStrong, color: colors.mutedInk }}>
                  {file === null ? 'Drop an image, or browse' : file.name}
                </div>
                <div style={{ ...type.caption, color: colors.mutedInk }}>
                  JPEG or PNG · at least 600 × 600 · up to 8 MB
                </div>
              </label>
              {problems.image !== undefined ? <ImageMessage rejection={problems.image} /> : null}
              <div style={{ ...type.meta, color: colors.navy, lineHeight: 1.55 }}>
                Without a photo a consumer has nothing to compare the pack against, which is exactly
                the case where a real code has been moved onto counterfeit goods. It is also shown
                publicly on every scan, so use the pack shot you would put on a shelf.
              </div>
            </div>
          </div>

          <div style={cardBox}>
            <div
              style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              <div style={{ ...type.cardTitle, color: colors.navy }}>
                How this appears on a scan
              </div>
              <div
                aria-label="Scan preview"
                style={{
                  borderRadius: 12,
                  background: colors.ground,
                  border: `1px solid ${colors.line}`,
                  padding: '14px 16px',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <div style={thumb}>
                  {preview !== null ? (
                    <img
                      src={preview}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : null}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ ...type.bodyBold, color: colors.navy }}>
                    {name.trim() || keepLine}
                  </div>
                  <div style={{ ...type.caption, color: colors.mutedInk }}>
                    {subline || keepLine}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

function release(url: string | null) {
  if (url !== null) URL.revokeObjectURL(url);
}

function CategoryOption({
  value,
  selected,
  onSelect,
}: {
  value: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const label = categoryLabel(value);
  return (
    <label
      style={{
        position: 'relative',
        boxSizing: 'border-box',
        padding: '12px 14px',
        borderRadius: 12,
        background: colors.card,
        border: selected ? `2px solid ${colors.brand}` : `1px solid ${colors.line}`,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: 'pointer',
      }}
    >
      <input
        type="radio"
        name="category"
        value={value}
        checked={selected}
        onChange={onSelect}
        aria-label={label}
        style={visuallyHidden}
      />
      <span
        aria-hidden="true"
        style={{
          width: 16,
          height: 16,
          boxSizing: 'border-box',
          borderRadius: 8,
          border: `2px solid ${selected ? colors.brand : radioRing}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {selected ? (
          <span style={{ width: 8, height: 8, borderRadius: 4, background: colors.brand }} />
        ) : null}
      </span>
      <span
        style={{ flexGrow: 1, ...(selected ? type.metaBold : type.metaStrong), color: colors.navy }}
      >
        {label}
      </span>
      {regulatedCategories.has(value) ? (
        <span style={{ ...pill, background: verdict.suspect.bg, color: verdict.suspect.fg }}>
          Regulated
        </span>
      ) : null}
    </label>
  );
}

function ImageMessage({ rejection }: { rejection: ImageRejection }) {
  if (rejection.kind === 'message') return <Rejection>{rejection.message}</Rejection>;
  return (
    <Rejection>
      That image is {rejection.width} × {rejection.height}, too small for a shopper to compare
      against the pack in their hand.{' '}
      <span style={{ color: colors.mutedInk }}>
        HEIC and WebP are not accepted here either; export as JPEG or PNG.
      </span>
    </Rejection>
  );
}

/** A field's own message, in the rejection's red. The board draws none, D10. */
function FieldMessage({ message }: { message: string | undefined }) {
  if (message === undefined) return null;
  return (
    <div role="alert" style={{ ...type.caption, color: verdict.unknown.fg }}>
      {message}
    </div>
  );
}

/**
 * Where a create or update failure lands. `validate` keys its details by
 * `body.<field>`: the three fields the form draws take theirs, and anything
 * else, or a failure with no details, goes to the box above the form.
 */
function fromFields(error: unknown): Problems {
  const problems: Problems = { fields: {} };
  if (!(error instanceof ApiError) || error.details === undefined) {
    problems.general = failureText(error);
    return problems;
  }
  const rest: string[] = [];
  for (const [path, message] of Object.entries(error.details)) {
    const field = path.replace(/^body\./, '');
    if (field === 'name' || field === 'description' || field === 'category')
      problems.fields[field] = message;
    else rest.push(message);
  }
  if (rest.length > 0) problems.general = rest.join(' ');
  return problems;
}

/** Where an image failure lands: the board's rejection, unless no answer came back. */
function fromImage(error: unknown): Problems {
  if (
    !(error instanceof ApiError) ||
    error.kind === 'network' ||
    error.kind === 'unauthenticated'
  ) {
    return { fields: {}, general: failureText(error) };
  }
  const { width, height } = error.details ?? {};
  if (error.code === 'image_too_small' && width !== undefined && height !== undefined) {
    return { fields: {}, image: { kind: 'too_small', width, height } };
  }
  return { fields: {}, image: { kind: 'message', message: error.message } };
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Try again.';
}

/** The unselected radio ring. Literal in the board and in no token. */
const radioRing = '#C5CBD6';

const column: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 };

const cardBox: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

const fieldColumn: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };

const fieldLabel: CSSProperties = { ...type.statLabel, color: colors.mutedInk };

const input: CSSProperties = {
  height: 44,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  padding: '0 14px',
  ...type.body,
  color: colors.navy,
};

/**
 * The board's description box has no `box-sizing`, so its 76px is the content:
 * 76 + 2 x 12 padding + 2 x 1 border is 102 outside, drawn here border-box.
 */
const textarea: CSSProperties = {
  height: 102,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  padding: '12px 14px',
  ...type.body,
  color: colors.mutedInk,
  lineHeight: 1.5,
  resize: 'none',
};

const pill: CSSProperties = {
  display: 'inline-flex',
  padding: '4px 12px',
  borderRadius: radius.chip,
  ...type.captionBold,
};

const dropZone: CSSProperties = {
  position: 'relative',
  height: 210,
  borderRadius: 14,
  background: colors.ground,
  border: `2px dashed ${colors.line}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  cursor: 'pointer',
};

const thumb: CSSProperties = {
  width: 48,
  height: 48,
  flexShrink: 0,
  boxSizing: 'border-box',
  overflow: 'hidden',
  borderRadius: 10,
  background: colors.card,
  border: `1px solid ${colors.line}`,
};

const visuallyHidden: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

const actionButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};

const cancelButton: CSSProperties = {
  ...actionButton,
  boxSizing: 'border-box',
  background: colors.card,
  border: `2px solid ${colors.navy}`,
  color: colors.navy,
};

const createButton: CSSProperties = {
  ...actionButton,
  background: colors.brand,
  color: colors.white,
  border: 'none',
};
